/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * Cell Chart Registry
 * 
 * This registry organizes LTE cell-level charts into logical groups.
 * It enables automatic rendering of charts in the CellKpisChartTab component.
 * 
 * Structure:
 * - Each chart group has a title and array of charts
 * - Each chart references its component and fullscreen key
 * - Charts are rendered in the order they appear in the registry
 * 
 * Usage:
 * - Import getCellChartGroups() to get all chart groups
 * - Use getCellFullscreenKeys() to get valid fullscreen keys
 */

import { ComponentType } from 'react';

// Chart component imports
import { 
  ErabEstablishmentCell,
  VolteEstablishmentCell,
  RrcConnectionEstablishmentCell,
  ErabDropRateCell, 
  VolteDropRateCell,
  DlGtpDataVolumeCell,
  UlGtpDataVolumeCell,
  HandinOverS1Cell,
  HandoutOverS1Cell,
  IntraFreqX1HoCell,
  InterFreqX1HoCell,
  LteCellAvailabilityCell,
  MaximumEndcCell,
  SgNbAdditionCell
} from '../../LTECell';

// NR Cell component imports
import { 
  SgNbAdditionNrCell, 
  MaximumEndcNrCell, 
  EndcUeSessionDropNrCell,
  NrCellAvailabilityNrCell,
  DlGtpDataVolumeNrCell,
  UlGtpDataVolumeNrCell,
  IntraCuHandoverSuccessNrCell
} from '../../NRCell';

// SA (NR Standalone) Cell component imports
import {
  RrcEstablishmentSaCell,
  SignalingConnectionSaCell,
  DataAccessibilitySaCell,
  DataAccessibilityPer5QiSaCell,
  QosFlowAccessibilitySaCell,
  VonrAccessibilitySaCell,
  DataRetainabilitySaCell,
  DataRetainabilityPer5QiSaCell,
  VonrRetainabilitySaCell,
  UeCallDropRateSaCell,
  InterCuHandoutSaCell,
  InterCuHandinSaCell,
  InterCuIntraFreqHandoutSaCell,
  InterCuInterFreqHandoutSaCell,
  IntraCuHandoverSaCell,
  GtpDataVolumeSaCell,
} from '../../SACell';

/**
 * Interface for individual chart configuration
 */
export interface CellChartConfig {
  component: ComponentType<{
    onFullscreenToggle: () => void;
    isFullscreen: boolean;
    cellName: string;  // Required: cell identifier for API
  }>;
  fullscreenKey: string;
  gridSize?: {  // Optional: custom grid sizing for individual charts
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

/**
 * Interface for chart group configuration
 */
export interface CellChartGroupConfig {
  groupTitle: string;
  technology: 'LTE' | 'NR' | 'SA';  // Technology type for filtering
  groupGridSize?: {  // Optional: custom grid sizing for the group container
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  charts: CellChartConfig[];
}

/**
 * LTE Cell Chart Groups
 * 
 * Organized by metric category:
 * - Accessibility: Connection establishment metrics
 * - Retainability: Connection drop metrics
 * - Data Volume: Data throughput and volume metrics
 * - Handover: Handover metrics
 * - Availability: Cell availability metrics
 */
const LTE_CELL_CHART_GROUPS: CellChartGroupConfig[] = [
  {
    groupTitle: "Accessibility",
    technology: 'LTE',
    charts: [
      {
        component: ErabEstablishmentCell,
        fullscreenKey: 'erab-establishment'
      },
      {
        component: VolteEstablishmentCell,
        fullscreenKey: 'volte-establishment'
      },
      {
        component: RrcConnectionEstablishmentCell,
        fullscreenKey: 'rrc-connection'
      }
    ]
  },
  {
    groupTitle: "Retainability",
    technology: 'LTE',
    charts: [
      {
        component: ErabDropRateCell,
        fullscreenKey: 'erab-drop-rate'
      },
      {
        component: VolteDropRateCell,
        fullscreenKey: 'volte-drop-rate'
      }
    ]
  },
  {
    groupTitle: "Data Volume",
    technology: 'LTE',
    charts: [
      {
        component: DlGtpDataVolumeCell,
        fullscreenKey: 'dl-gtp-data-volume'
      },
      {
        component: UlGtpDataVolumeCell,
        fullscreenKey: 'ul-gtp-data-volume'
      }
    ]
  },
  {
    groupTitle: "Handover",
    technology: 'LTE',
    charts: [
      {
        component: HandinOverS1Cell,
        fullscreenKey: 'handin-over-s1'
      },
      {
        component: HandoutOverS1Cell,
        fullscreenKey: 'handout-over-s1'
      },
      {
        component: IntraFreqX1HoCell,
        fullscreenKey: 'intra-freq-x1-ho'
      },
      {
        component: InterFreqX1HoCell,
        fullscreenKey: 'inter-freq-x1-ho'
      }
    ]
  },
  {
    groupTitle: "Availability",
    technology: 'LTE',
    charts: [
      {
        component: LteCellAvailabilityCell,
        fullscreenKey: 'lte-cell-availability'
      }
    ]
  },
  {
    groupTitle: "ENDC",
    technology: 'LTE',
    charts: [
      {
        component: MaximumEndcCell,
        fullscreenKey: 'maximum-endc'
      },
      {
        component: SgNbAdditionCell,
        fullscreenKey: 'sgnb-addition'
      }
    ]
  }
];

/**
 * NR Cell Chart Groups
 * 
 * Contains NR (5G) cell-level charts for devices with node type NR (RCP, CU).
 * Charts use data from NR_FR2_CELL_KPI table.
 * 
 * Organized by metric category:
 * - Accessibility: Connection establishment and setup metrics
 * - Capacity: Resource utilization and capacity metrics
 */
const NR_CELL_CHART_GROUPS: CellChartGroupConfig[] = [
  {
    groupTitle: "Accessibility",
    technology: 'NR',
    groupGridSize: { xs: 12, md: 6 },  // Half width on desktop for side-by-side layout
    charts: [
      {
        component: SgNbAdditionNrCell,
        fullscreenKey: 'sgnb-addition-nr'
      }
    ]
  },
  {
    groupTitle: "Capacity",
    technology: 'NR',
    groupGridSize: { xs: 12, md: 6 },  // Half width on desktop for side-by-side layout
    charts: [
      {
        component: MaximumEndcNrCell,
        fullscreenKey: 'maximum-endc-nr'
      }
    ]
  },
  {
    groupTitle: "Retainability",
    technology: 'NR',
    groupGridSize: { xs: 12, md: 6 },  // Half width on desktop for side-by-side layout with Availability
    charts: [
      {
        component: EndcUeSessionDropNrCell,
        fullscreenKey: 'endc-ue-session-drop-nr'
      }
    ]
  },
  {
    groupTitle: "Availability",
    technology: 'NR',
    groupGridSize: { xs: 12, md: 6 },  // Half width on desktop for side-by-side layout with Retainability
    charts: [
      {
        component: NrCellAvailabilityNrCell,
        fullscreenKey: 'nr-cell-availability-nr'
      }
    ]
  },
  {
    groupTitle: "Data Volume",
    technology: 'NR',
    // Full width - no groupGridSize means it takes the entire row (12 columns)
    charts: [
      {
        component: DlGtpDataVolumeNrCell,
        fullscreenKey: 'dl-gtp-data-volume-nr'
      },
      {
        component: UlGtpDataVolumeNrCell,
        fullscreenKey: 'ul-gtp-data-volume-nr'
      }
    ]
  },
  {
    groupTitle: "Handover",
    technology: 'NR',
    // Full width - no groupGridSize means it takes the entire row (12 columns)
    charts: [
      {
        component: IntraCuHandoverSuccessNrCell,
        fullscreenKey: 'intra-cu-handover-success-nr'
      }
    ]
  }
];

// ============================================================
// SA (NR Standalone) Cell Chart Groups
// ============================================================

const SA_CELL_CHART_GROUPS: CellChartGroupConfig[] = [
  {
    groupTitle: "Accessibility",
    technology: 'SA',
    charts: [
      { component: RrcEstablishmentSaCell,        fullscreenKey: 'rrc-establishment-sa' },
      { component: SignalingConnectionSaCell,     fullscreenKey: 'signaling-connection-sa' },
      { component: DataAccessibilitySaCell,       fullscreenKey: 'data-accessibility-sa' },
      { component: DataAccessibilityPer5QiSaCell, fullscreenKey: 'data-accessibility-per-5qi-sa' },
      { component: QosFlowAccessibilitySaCell,    fullscreenKey: 'qos-flow-accessibility-sa' },
      { component: VonrAccessibilitySaCell,       fullscreenKey: 'vonr-accessibility-sa' },
    ],
  },
  {
    groupTitle: "Retainability",
    technology: 'SA',
    charts: [
      { component: DataRetainabilitySaCell,       fullscreenKey: 'data-retainability-sa' },
      { component: DataRetainabilityPer5QiSaCell, fullscreenKey: 'data-retainability-per-5qi-sa' },
      { component: VonrRetainabilitySaCell,       fullscreenKey: 'vonr-retainability-sa' },
      { component: UeCallDropRateSaCell,          fullscreenKey: 'ue-call-drop-rate-sa' },
    ],
  },
  {
    groupTitle: "Handover",
    technology: 'SA',
    charts: [
      { component: InterCuHandoutSaCell,          fullscreenKey: 'inter-cu-handout-sa' },
      { component: InterCuHandinSaCell,           fullscreenKey: 'inter-cu-handin-sa' },
      { component: InterCuIntraFreqHandoutSaCell, fullscreenKey: 'inter-cu-intra-freq-handout-sa' },
      { component: InterCuInterFreqHandoutSaCell, fullscreenKey: 'inter-cu-inter-freq-handout-sa' },
      { component: IntraCuHandoverSaCell,         fullscreenKey: 'intra-cu-handover-sa' },
    ],
  },
  {
    groupTitle: "Data Volume",
    technology: 'SA',
    charts: [
      { component: GtpDataVolumeSaCell,           fullscreenKey: 'gtp-data-volume-sa' },
    ],
  },
];

/**
 * Get cell chart groups, optionally filtered by technology
 * 
 * @param technology - Filter by 'LTE', 'NR', or return 'ALL' (default)
 * @returns Array of chart group configurations
 */
export const getCellChartGroups = (technology: 'LTE' | 'NR' | 'SA' | 'ALL' = 'ALL'): CellChartGroupConfig[] => {
  if (technology === 'LTE') {
    return LTE_CELL_CHART_GROUPS;
  }
  if (technology === 'NR') {
    return NR_CELL_CHART_GROUPS;
  }
  if (technology === 'SA') {
    return SA_CELL_CHART_GROUPS;
  }
  // Return all groups (LTE + NR + SA)
  return [...LTE_CELL_CHART_GROUPS, ...NR_CELL_CHART_GROUPS, ...SA_CELL_CHART_GROUPS];
};

/**
 * Get all valid fullscreen keys for cell charts
 * 
 * @param technology - Filter by 'LTE', 'NR', or return 'ALL' (default)
 * @returns Array of fullscreen key strings
 */
export const getCellFullscreenKeys = (technology: 'LTE' | 'NR' | 'SA' | 'ALL' = 'ALL'): string[] => {
  const groups = getCellChartGroups(technology);
  return groups.flatMap(group =>
    group.charts.map(chart => chart.fullscreenKey)
  );
};

/**
 * Get chart configuration by fullscreen key
 * 
 * @param key - The fullscreen key to search for
 * @param technology - Filter by 'LTE', 'NR', or search 'ALL' (default)
 * @returns Chart configuration or undefined if not found
 */
export const getCellChartByKey = (
  key: string, 
  technology: 'LTE' | 'NR' | 'SA' | 'ALL' = 'ALL'
): CellChartConfig | undefined => {
  const groups = getCellChartGroups(technology);
  for (const group of groups) {
    const chart = group.charts.find(c => c.fullscreenKey === key);
    if (chart) return chart;
  }
  return undefined;
};

/**
 * Get Technology from Node Type (Cell Level)
 * 
 * Maps device node type to radio access technology for cell-level chart filtering.
 * This ensures only relevant charts are shown for each cell type (LTE vs NR).
 * 
 * @param nodeType - Device node type (SN, BBU, RCP, CU, etc.)
 * @returns Technology type ('LTE' or 'NR')
 * 
 * @example
 * getCellTechnologyFromNodeType('SN')   // Returns 'LTE' (LTE eNodeB)
 * getCellTechnologyFromNodeType('BBU')  // Returns 'LTE' (LTE Baseband Unit)
 * getCellTechnologyFromNodeType('RCP')  // Returns 'NR' (5G Radio Control Point)
 * getCellTechnologyFromNodeType('CU')   // Returns 'NR' (5G Central Unit)
 */
export function getCellTechnologyFromNodeType(nodeType: string): 'LTE' | 'NR' {
  const normalizedType = nodeType?.toUpperCase() || '';
  
  // NR (5G) device types
  if (normalizedType === 'RCP' || normalizedType === 'CU') {
    return 'NR';
  }
  
  // LTE (4G) device types (SN, BBU) - also default fallback
  return 'LTE';
}
